import { AgGridAngular } from 'ag-grid-angular';
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColDef,
    GridOptions,
    ModuleRegistry,
    RowGroupingDisplayType,
} from 'ag-grid-community';
import {
    ClipboardModule,
    ContextMenuModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule,
} from 'ag-grid-enterprise';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    inject,
} from '@angular/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import {
    LOCALISED_CURRENCY_VALUE_FORMATTER,
    numberColorCellClass,
    numberFieldPropertyComparator,
} from '@wdx/shared/components/ag-grid';
import {
    WdxButtonModule,
    WdxButtonStyle,
} from '@wdx/shared/components/wdx-button';
import { WdxDropdownModule } from '@wdx/shared/components/wdx-dropdown';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { WdxSpinnerModule } from '@wdx/shared/components/wdx-spinner';
import {
    FriendlyProblemDetails,
    IMeLocale,
    TranslationsService,
    WdxAgGridThemeDirective,
    WdxNumberService,
} from '@wdx/shared/utils';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
    PortfolioLoadingStates,
    PortfolioNotLoadedComponent,
} from '../portfolio-not-loaded/portfolio-not-loaded.component';
import { PositionsData } from '../positions';

ModuleRegistry.registerModules([
    AllCommunityModule,
    ClientSideRowModelModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule,
    ClipboardModule,
    ContextMenuModule,
]);

@Component({
    selector: 'wdx-portfolio-top-movers',
    imports: [
        AgGridAngular,
        WdxAgGridThemeDirective,
        WdxSpinnerModule,
        WdxDropdownModule,
        WdxIconModule,
        NgbDropdownModule,
        WdxButtonModule,
        PortfolioNotLoadedComponent,
    ],
    templateUrl: './top-movers.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'h-100' },
})
export class TopMoversComponent {
    private changeDetectorRef = inject(ChangeDetectorRef);
    private translationsService = inject(TranslationsService);
    private localeService = inject(IMeLocale);
    private numberService = inject(WdxNumberService);

    @Input() dataTypeLabel = 'Position';
    @Input() dataConnectionError?: FriendlyProblemDetails = undefined;
    @Input() set topMoverData(value: PositionsData[] | null | undefined) {
        if (!value) {
            this.status = 'loading';
            return;
        }

        this.status = 'loaded';
        this.rowData = value;
        if (!this.rowData.length) {
            this.status = 'noData';
        }

        this.changeDetectorRef.markForCheck();
    }

    public status: PortfolioLoadingStates = 'loading';
    public readonly WdxButtonStyle = WdxButtonStyle;
    public loading = true;
    public rowData?: PositionsData[];
    public groupDisplayType: RowGroupingDisplayType = 'groupRows';

    public gridOptions: GridOptions = {
        domLayout: 'autoHeight',
        paginationPageSize: 20,
        defaultColDef: {
            flex: 1,
            sortable: true,
            contextMenuItems: ['copy'],
        },
    };

    public colDefs: ColDef[] = [
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_TOP_MOVERS_NAME',
            ),
            field: 'name',
            flex: 2,
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_TOP_MOVERS_VALUATION',
            ),
            headerClass: 'ag-right-aligned-header',
            field: 'value',
            cellClass: 'text-end',
            comparator: numberFieldPropertyComparator('value', 'value'),
            valueFormatter: LOCALISED_CURRENCY_VALUE_FORMATTER(
                this.numberService,
                () => this.localeService.getMeLocale(),
            ),
        },
        {
            headerName: this.translationsService.translateTokenisedString(
                'PORTFOLIO_TOP_MOVERS_GAIN_LOSS',
            ),
            field: 'gainloss',
            headerClass: 'ag-right-aligned-header',
            sort: 'desc',
            cellClass: numberColorCellClass('text-end', 'gainloss', 'value'),
            comparator: numberFieldPropertyComparator('gainloss', 'value'),
            valueFormatter: LOCALISED_CURRENCY_VALUE_FORMATTER(
                this.numberService,
                () => this.localeService.getMeLocale(),
            ),
        },
    ];
}
